/**
 * The session a space is working with, and a comment's way to it (RFC 0301
 * §3d, S10).
 *
 * A comment made on a page names nobody most of the time, and somebody has to
 * read it. The rule is the compact composer's: the space's current session is
 * the one the strip shows, and a comment on any page in that space goes there,
 * as if typed into the strip. When the space has no session the comment starts
 * one, the way the strip's new thread does, and the reply is its first turn.
 *
 * WHY A CONTEXT. The shell resolves the strip's target once, against the
 * working set and the live catalog, and three surfaces write root comments: the
 * document column, a host-realm app, and the sandboxed bridge. Each of them is
 * panes deep and none can see the working set. Handing the resolved answer down
 * means the box and the delivery agree by construction, which is the same
 * reason the strip is handed `target` rather than an id.
 *
 * WHY THE CORE DOES NOT DECIDE. `offerComment` takes a session id and does the
 * delivery; it cannot see a space, and a space is a window thing (RFC 0281).
 * The claim it replaced tried to decide from the pairings alone, and picked a
 * session in another space that the person could not see.
 */

import type { Repo } from '@automerge/automerge-repo'
import { COMMENT_ANSWER_RULE, offerComment } from '@mod/core/model'
import { createContext, useContext } from 'react'

export interface SpaceSession {
  /** The space's current session: what the composer strip shows. Null when
   *  the space has none, or the window has no space. */
  sessionId: string | null
  /** Start a session in this space with a first message, resolving to its id,
   *  or to null when it could not be started. Absent when there is nowhere to
   *  start one (no project, no space). */
  start?: (text: string) => Promise<string | null>
}

const SpaceSessionContext = createContext<SpaceSession>({ sessionId: null })

export const SpaceSessionProvider = SpaceSessionContext.Provider

export function useSpaceSession(): SpaceSession {
  return useContext(SpaceSessionContext)
}

/**
 * The first message of a session a comment starts. It carries the comment
 * itself, because the session has no log yet for a signal to land on, and
 * says where to answer so the reply lands in the thread rather than here.
 */
export function startingMessage(comment: {
  id: string
  body: string
  documentId?: string
}): string {
  const where = comment.documentId
    ? ` on document ${comment.documentId}`
    : ' on a page in this space'
  return (
    `A comment${where} started this session:\n\n${comment.body.trim()}\n\n` +
    `Answer it in its thread with comment action:reply id:${comment.id}. ` +
    COMMENT_ANSWER_RULE
  )
}

/**
 * Deliver a root comment that names nobody. Resolves to the session that has
 * it, or null when the space has none and cannot start one.
 */
export async function routeComment(
  repo: Repo,
  projectId: string,
  space: SpaceSession,
  comment: { id: string; body: string; documentId?: string },
): Promise<string | null> {
  if (space.sessionId) {
    const out = await offerComment(repo as never, projectId, {
      commentId: comment.id,
      sessionId: space.sessionId,
    })
    return out.answeredBy
  }
  if (!space.start) return null
  return space.start(startingMessage(comment))
}
